import React from "react";
import {
  Box,
  Typography,
  CardActionArea,
  Card,
  ListItemIcon,
  Theme,
} from "@mui/material";
import {
  ComponentTemplateDto,
  ComponentTemplatePrivacyType,
} from "@growth-machine-llc/stridist-api-client";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@mui/material";
import { ComponentTemplateCategoryBadge } from "./ComponentTemplateCategoryBadge";
import { ComponentTemplatePrivacyBadge } from "./ComponentTemplatePrivacyBadge";
import { ComponentTemplateMenu } from "./ComponentTemplateMenu";
import { useUpdateLibraryComponentTemplate } from "./hooks/useUpdateLibraryComponentTemplate";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { ComponentIcon } from "../program-component/ComponentIcon";
import { SelectComponentIconsMenu } from "../menu/SelectComponentIconsMenu";

type StyleProps = {
  isPrivate?: boolean;
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  actionArea: {
    padding: theme.spacing(1.5),
    justifyContent: "space-between",
  },

  name: {
    fontWeight: 600,
    fontSize: 16,
    marginBottom: theme.spacing(0.5),
  },
  category: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  info: {
    height: "100%",
    display: "flex",
    flexDirection: "column-reverse",
    justifyContent: "space-between",
  },
  mobileCard: {
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[2],
    position: "relative",
  },
  menuButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
  },

  icon: {
    padding: theme.spacing(1),
    margin: theme.spacing(0, 1, 0, -1),
    cursor: "pointer",
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: ({ isPrivate }) =>
        isPrivate && theme.palette.selected.main,
    },
  },
  iconsMenu: {
    width: theme.spacing(40),
    zIndex: "9999 !important" as any,
  },
}));

export interface ComponentTemplateCardViewProps {
  template: ComponentTemplateDto;
  onContentClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
  onEdit?: () => void;
}

export function ComponentTemplateCardView(
  props: ComponentTemplateCardViewProps,
) {
  const { template, onContentClick, onEdit } = props;
  const isPrivate = template.privacy === ComponentTemplatePrivacyType.PRIVATE;
  const s = useStyles({ isPrivate });

  const { mutate: updateTemplate } = useUpdateLibraryComponentTemplate(
    template.id,
  );

  const iconsMenuState = usePopupState({
    variant: "popover",
    popupId: "iconsMenu",
  });

  const handleUpdateIcon = (iconName: string) => {
    iconsMenuState.close();
    if (iconName === template.iconName) {
      return;
    }
    updateTemplate({
      ...template,
      iconName,
    });
  };

  return (
    <Card
      className={s.mobileCard}
      onClick={onContentClick}
      data-template-id={template.id}
    >
      <Box className={s.menuButton} onClick={(e) => e.stopPropagation()}>
        {template.privacy === ComponentTemplatePrivacyType.PRIVATE && (
          <ComponentTemplateMenu component={template} onEdit={onEdit} />
        )}
      </Box>
      <CardActionArea className={s.actionArea}>
        <Box
          display="flex"
          justifyContent="space-between"
          gap={2}
          flexDirection={"column"}
        >
          <Box display="flex" alignItems="center">
            <Box
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <ListItemIcon
                onClick={(e) => e.stopPropagation()}
                children={
                  <ComponentIcon
                    variant="icon"
                    componentData={{
                      iconName: template.iconName,
                      type: template.type,
                    }}
                  />
                }
                className={s.icon}
                {...(template.coachId ? bindTrigger(iconsMenuState) : {})}
              />
            </Box>
            <Typography className={s.name}>{template.title}</Typography>
          </Box>
          <Box className={s.info}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={0.5}
            >
              <ComponentTemplatePrivacyBadge
                template={template}
                size={24}
                showLabel={true}
              />
              <ComponentTemplateCategoryBadge category={template.type} />
            </Box>
          </Box>
        </Box>
      </CardActionArea>

      <Box onClick={(e) => e.stopPropagation()}>
        <SelectComponentIconsMenu
          {...bindMenu(iconsMenuState)}
          className={s.iconsMenu}
          anchorOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          onSubmit={(iconName) => {
            handleUpdateIcon(iconName);
          }}
          hideApplyToAll
          componentData={{
            type: template.type,
            iconName: template.iconName,
          }}
        />
      </Box>
    </Card>
  );
}
